import React, { createContext, useContext, useState, useEffect } from "react"
import { auth } from "../lib/supabase"

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      // Check for Supabase session first
      const { session } = await auth.getSession()

      if (session?.user) {
        setUser(session.user)
      } else {
        // Check for local guest session
        const guestSession = localStorage.getItem("guest_session")
        if (guestSession) {
          try {
            const guestUser = JSON.parse(guestSession)
            setUser(guestUser)
          } catch (error) {
            localStorage.removeItem("guest_session")
          }
        }
      }

      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const {
      data: { subscription },
    } = auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        // Clear any guest session when real user signs in
        localStorage.removeItem("guest_session")
        setUser(session.user)
      } else {
        setUser(null)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const login = async (email, password) => {
    try {
      const { data, error } = await auth.signIn(email, password)

      if (error) {
        return {
          success: false,
          error: error.message || "Login failed",
        }
      }

      return { success: true, user: data.user }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Login failed",
      }
    }
  }

  const register = async (email, password, name) => {
    try {
      const { data, error } = await auth.signUp(email, password, { name })

      if (error) {
        return {
          success: false,
          error: error.message || "Registration failed",
        }
      }

      return { success: true, user: data.user }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Registration failed",
      }
    }
  }

  const createGuestSession = async () => {
    try {
      // Create a local guest session without Supabase auth
      const guestUser = {
        id: `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        email: null,
        is_anonymous: true,
        user_metadata: {
          name: "Guest User",
        },
        created_at: new Date().toISOString(),
      }

      // Store guest session locally
      localStorage.setItem("guest_session", JSON.stringify(guestUser))
      setUser(guestUser)

      return { success: true, user: guestUser }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to create guest session",
      }
    }
  }

  const convertGuestToUser = async (email, password, name) => {
    // For Supabase, we'll need to handle this differently
    // This would require custom logic or Edge Functions
    return {
      success: false,
      error: "Guest conversion not yet implemented with Supabase",
    }
  }

  const logout = async () => {
    try {
      // Clear guest session
      localStorage.removeItem("guest_session")

      // Sign out from Supabase if authenticated
      if (user && !user.is_anonymous) {
        await auth.signOut()
      }

      setUser(null)
    } catch (error) {
      console.error("Logout error:", error)
    }
  }

  const value = {
    user,
    loading,
    login,
    register,
    createGuestSession,
    convertGuestToUser,
    logout,
    isAuthenticated: !!user,
    isGuest: user?.is_anonymous || false,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
